import { useMemo } from 'react';
import { Platform, StyleSheet } from 'react-native';
import { GTFonts, useTheme, type Theme } from '../../../../theme';
import { usePickerItemColor } from '../../../../hooks/usePickerItemColor';
import { borderWidth, fs, s, v } from '../../../../utils/libs';

/**
 * Shared styling for the extracted product form field components (US3,
 * T053). The add-product look follows BackOfficeAddProductScreen.
 */

export const createProductFormFieldStyles = (theme: Theme) => {
  // `textDark` is an optional theme extension mirrored from NewProduct.tsx,
  // which historically typed its theme as `any`.
  const textDark = (theme.colors as any).textDark ?? theme.colors.text;
  return StyleSheet.create({
    sectionTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(16),
      lineHeight: fs(20),
      color: theme.colors.text,
      ...(Platform.OS === 'web' && {
        fontSize: fs(18),
        lineHeight: fs(22),
      }),
    },
    sectionHeader: {
      gap: v(2),
    },
    // Extra space above a group heading inside the shared form card, so each
    // group reads as its own block without a card break.
    groupStart: {
      marginTop: v(10),
    },
    sectionHint: {
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(18),
      color: theme.colors.textSecondary,
    },
    field: {
      gap: v(6),
    },
    fieldRow: {
      flexDirection: 'row',
      gap: s(12),
    },
    fieldHalf: {
      flex: 1,
      minWidth: 0,
      gap: v(6),
      // Keeps both inputs of a row aligned when one label wraps.
      justifyContent: 'flex-end',
    },
    fieldLabel: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(13),
      lineHeight: fs(16),
      color: theme.colors.textGray700,
    },
    fieldLabelOptional: {
      fontFamily: GTFonts.regular,
      color: theme.colors.textSecondary,
    },
    fieldHint: {
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
      color: theme.colors.textSecondary,
    },
    accordionHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(8),
    },
    accordionHeaderBody: {
      flex: 1,
      gap: v(2),
    },
    accordionBody: {
      gap: v(12),
      paddingTop: v(2),
    },
    accordionTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(16),
      lineHeight: fs(18),
      color: textDark,
      ...(Platform.OS === 'web' && {
        fontSize: fs(18),
      }),
    },
    accordionChevron: {
      color: theme.colors.text,
      fontSize: fs(20),
      lineHeight: fs(20),
      margin: 0,
      padding: 0,
    },
    input: {
      backgroundColor: theme.colors.surface,
      height: v(50),
      borderRadius: theme.borderRadius.md,
      ...(Platform.OS === 'web' && {
        height: v(56),
      }),
    },
    pickerContainer: {
      borderWidth: 1,
      borderColor: theme.colors.borderGray,
      borderRadius: theme.borderRadius.md,
      justifyContent: 'center',
      overflow: 'hidden',
      backgroundColor: theme.colors.surface,
      ...(Platform.OS === 'web' && {
        height: v(56),
      }),
    },
    picker: {
      fontSize: fs(14),
      color: theme.colors.text,
      ...(Platform.OS === 'web' && {
        height: v(56),
        borderWidth: 0,
        backgroundColor: 'transparent',
        outlineStyle: 'none',
        outlineWidth: 0,
        outlineColor: 'transparent',
        paddingHorizontal: 12,
        fontSize: fs(14),
      }),
    } as any,
    priceTypeTiles: {
      flexDirection: 'row',
      gap: s(10),
    },
    priceTypeTile: {
      flex: 1,
      gap: v(4),
      paddingVertical: v(10),
      paddingHorizontal: s(12),
      borderRadius: s(12),
      borderWidth: borderWidth(1.5),
      borderColor: theme.colors.borderGray,
      backgroundColor: theme.colors.surface,
    },
    priceTypeTileSelected: {
      borderColor: theme.colors.primaryDark,
      backgroundColor: theme.colors.primarySurface,
    },
    priceTypeTileHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(8),
    },
    priceTypeTileLabel: {
      flex: 1,
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(18),
      color: theme.colors.text,
    },
    priceTypeTileHint: {
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
      color: theme.colors.textSecondary,
    },
    priceTypeRadio: {
      width: s(18),
      height: s(18),
      borderRadius: theme.borderRadius.round,
      borderWidth: borderWidth(1.5),
      borderColor: theme.colors.borderGray,
      backgroundColor: theme.colors.surface,
    },
    priceTypeRadioSelected: {
      borderWidth: s(5),
      borderColor: theme.colors.primaryDark,
    },
    unitGroupRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(8),
    },
    unitGroupLabel: {
      width: s(52),
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
      color: theme.colors.textSecondary,
    },
    chipWrap: {
      flex: 1,
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: s(8),
    },
    attributeChipContainer: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: s(8),
    },
    // One chip style for unit choices and attribute names.
    choiceChip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(4),
      minHeight: v(36),
      paddingHorizontal: s(12),
      paddingVertical: v(6),
      borderRadius: theme.borderRadius.round,
      borderWidth: borderWidth(1),
      borderColor: theme.colors.borderGray,
      backgroundColor: theme.colors.surface,
    },
    choiceChipSelected: {
      borderColor: theme.colors.primaryDark,
      backgroundColor: theme.colors.primarySurface,
    },
    choiceChipText: {
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(16),
      color: theme.colors.text,
    },
    choiceChipTextSelected: {
      fontFamily: GTFonts.semibold,
      color: theme.colors.primaryDark,
    },
    choiceChipAction: {
      borderColor: theme.colors.primaryDark,
    },
    choiceChipActionText: {
      fontFamily: GTFonts.semibold,
      color: theme.colors.primaryDark,
    },
    variantEmptyState: {
      alignItems: 'center',
      gap: v(6),
      paddingVertical: v(20),
      paddingHorizontal: s(16),
      borderRadius: s(12),
      borderWidth: borderWidth(1),
      borderStyle: 'dashed',
      borderColor: theme.colors.borderGray,
    },
    variantEmptyTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(18),
      color: theme.colors.text,
      marginTop: v(4),
    },
    variantEmptyHint: {
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(18),
      color: theme.colors.textSecondary,
      textAlign: 'center',
      maxWidth: s(260),
    },
    // Variants are a list of rows; only the open row shows its fields.
    variantListRow: {
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: theme.colors.borderGray200,
    },
    variantRowHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(10),
      minHeight: v(56),
      paddingVertical: v(12),
    },
    variantRowBody: {
      flex: 1,
      gap: v(2),
    },
    variantRowTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(15),
      lineHeight: fs(20),
      color: theme.colors.text,
    },
    variantRowTitleDeleted: {
      color: theme.colors.textSecondary,
      textDecorationLine: 'line-through',
    },
    variantRowSummary: {
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(16),
      color: theme.colors.textSecondary,
    },
    variantRowPrice: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(15),
      lineHeight: fs(20),
      color: theme.colors.text,
    },
    variantRowPriceMissing: {
      fontSize: fs(13),
      lineHeight: fs(16),
      color: theme.colors.primaryDark,
    },
    variantRowAction: {
      paddingVertical: v(8),
      paddingHorizontal: s(8),
    },
    variantRowActionText: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(18),
      color: theme.colors.primaryDark,
    },
    variantRowFields: {
      gap: v(12),
      paddingBottom: v(12),
    },
    subAccordionHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(8),
      paddingVertical: v(6),
    },
    subAccordionTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(18),
      color: theme.colors.text,
    },
    variantRemoveButton: {
      flexDirection: 'row',
      alignItems: 'center',
      alignSelf: 'flex-start',
      gap: s(6),
      paddingVertical: v(8),
      paddingRight: s(8),
    },
    variantRemoveText: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(18),
      color: theme.colors.errorDark,
    },
    variantAddButton: {
      borderRadius: s(12),
      borderColor: theme.colors.primaryDark,
    },
    variantBarcodeList: {
      padding: s(10),
      borderRadius: theme.borderRadius.md,
      backgroundColor: theme.colors.surfaceSecondary,
      gap: v(6),
    },
    variantBarcodeListTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(18),
      color: theme.colors.text,
    },
    variantBarcodeListItem: {
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(16),
      color: theme.colors.textSecondary,
    },
  });
};

export type ProductFormFieldStyles = ReturnType<
  typeof createProductFormFieldStyles
>;

/**
 * Theme bundle used by every extracted field component: outline props,
 * web-only placeholder softening, picker item colors, and shared styles.
 */
export const useProductFormFieldTheme = () => {
  const { theme } = useTheme();
  const { pickerItemColor, pickerItemColorSecondary } = usePickerItemColor();

  const styles = useMemo(() => createProductFormFieldStyles(theme), [theme]);

  const inputOutlineProps = useMemo(
    () => ({
      outlineColor: theme.colors.borderGray,
      activeOutlineColor: theme.colors.primary,
    }),
    [theme],
  );

  const webPlaceholderTheme = useMemo(() => {
    if (Platform.OS === 'web') {
      const hexToRgba = (hex: string, alpha: number) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
      };

      const placeholderColor = theme.colors.textSecondary
        ? hexToRgba(theme.colors.textSecondary, 0.55)
        : 'rgba(128, 128, 128, 0.55)';

      return {
        theme: {
          fonts: {
            regular: {
              fontFamily: GTFonts.regular,
              fontWeight: '400' as any,
            },
          },
          colors: {
            placeholder: placeholderColor,
            onSurfaceVariant: placeholderColor,
          },
        },
      };
    }
    return {};
  }, [theme.colors.textSecondary]);

  return {
    theme,
    styles,
    inputOutlineProps,
    webPlaceholderTheme,
    pickerItemColor,
    pickerItemColorSecondary,
  };
};
